import React, { useRef, useEffect, MutableRefObject, useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router';
import { KibAlert } from '@chewy/kib-messaging-react';
import { PetInterface } from 'types/pet';
import { getPetWithOwnerName, getPet, getActivePets, updatePetProfile } from 'api/petApi';
import AutoshipEffectMessage from 'components/autoship-effect-message/AutoshipEffectMessage';
import AnalyticsContext from '../../../context/AnalyticsContext';
import log from '../../../util/log';
import FetchError from '../../../types/http';
import {
  ApiPetProfile,
  ApiPetProfileStatusEnum,
} from '../../../api/generated-client/PetProfile-Service-A.v1/models';
import { handleAuthError } from '../../../util/auth';
import { getApiPetProfileFromInterface } from '../utils/getApiPetProfileFromInterface';
import { getLandingStepCopy } from '../copy';
import { ManagePetCard } from '../ManagePetCard/ManagePetCard';
import cookie from '../../../util/cookie';
import '@chewy/kib-messaging-styles/dist/styles/kib-alert.css';
import './manage-pet-landing.scss';

export type ManagePetLandingProps = {
  pet: PetInterface;
  setStatusChoice: React.Dispatch<React.SetStateAction<ApiPetProfileStatusEnum | undefined>>;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ManagePetLanding = ({ pet, setStatusChoice, setShowError }: ManagePetLandingProps) => {
  const classNamePrefix = `manage-pet-landing`;

  const {
    errorMessage,
    memorialTitle,
    memorialSubtitle,
    removeTitle,
    removeSubtitle,
  } = getLandingStepCopy(pet);

  const location = useLocation();
  const analytics = useContext(AnalyticsContext);
  const queryClient = useQueryClient();
  const choice: MutableRefObject<ApiPetProfileStatusEnum | undefined> = useRef();
  const clientErrors: MutableRefObject<number> = useRef(0);
  const serverErrors: MutableRefObject<number> = useRef(0);

  useEffect(() => {
    analytics.trackRainbow({
      name: 'pet profile rainbow - viewed',
      category: 'pet-profile-rainbow',
      action: 'impression',
      label: 'pet_profile_rainbow_impression',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const determineRetry = (failureCount: number, error: FetchError): boolean => {
    if (error.status >= 400 && error.status < 500) {
      clientErrors.current = clientErrors.current + 1;
      return 3 - clientErrors.current > 0;
    } else if (error.status >= 500) {
      serverErrors.current = serverErrors.current + 1;
      return false;
    } else {
      return false;
    }
  };

  const invalidateQueries = () => {
    const invalidQueries = [
      queryClient.removeQueries(getActivePets.key),
      queryClient.removeQueries([getPet.key, pet.id]),
      queryClient.removeQueries([getPetWithOwnerName.key, pet.id]),
    ];
    return Promise.all(invalidQueries);
  };

  const updatePet = useMutation(
    (payload: ApiPetProfile) => updatePetProfile(Number(payload.id), payload),
    {
      onSuccess: () => {
        try {
          invalidateQueries();
        } catch (error) {
          log.error('Failed to invalidate cache on memorialize/remove profile:', error);
        }
        choice.current && setStatusChoice(choice.current);
      },
      onError: (error: FetchError) => {
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname); // TODO check this
        if (clientErrors.current >= 3 || serverErrors.current >= 2) {
          setShowError(true);
        }
      },
      retry: determineRetry,
    },
  );
  const hasError = updatePet.isError;
  const isLoading = updatePet.isLoading;

  const handleOnClick = (status: ApiPetProfileStatusEnum) => {
    choice.current = status;
    updatePet.mutate({
      ...getApiPetProfileFromInterface(pet),
      status: status,
    });
  };

  return (
    <div className={`${classNamePrefix}`}>
      {hasError && !isLoading && (
        <KibAlert
          className={`${classNamePrefix}__error-message`}
          data-testid={`${classNamePrefix}__error-message`}
          severity="error"
        >
          {errorMessage}
        </KibAlert>
      )}
      <AutoshipEffectMessage />
      <ManagePetCard
        className={`${classNamePrefix}__memorial-card`}
        data-testid={`${classNamePrefix}__memorial-card`}
        title={memorialTitle}
        subtitle={memorialSubtitle}
        onClick={() => {
          analytics.trackRainbow({
            name: 'pet profile rainbow - clicked',
            category: 'pet-profile-rainbow',
            action: 'click',
            label: 'pet_profile_rainbow',
          });
          handleOnClick(ApiPetProfileStatusEnum.Inactive);
          cookie.set(`memorialized`, `${pet.name}|${pet.id}`, {
            path: '/',
          });
        }}
        loading={isLoading && choice.current === ApiPetProfileStatusEnum.Inactive}
        disabled={isLoading}
      />
      <ManagePetCard
        className={`${classNamePrefix}__remove-card`}
        data-testid={`${classNamePrefix}__remove-card`}
        title={removeTitle}
        subtitle={removeSubtitle}
        onClick={() => {
          analytics.trackRainbow({
            name: 'pet profile rainbow - clicked',
            category: 'pet-profile-rainbow',
            action: 'click',
            label: 'pet_profile_rainbow_remove',
          });
          handleOnClick(ApiPetProfileStatusEnum.Deleted);
          cookie.set(`removed`, `${pet.name}|${pet.id}`, {
            path: '/',
          });
        }}
        loading={isLoading && choice.current === ApiPetProfileStatusEnum.Deleted}
        disabled={isLoading}
      />
    </div>
  );
};
